import classes from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../../contexts/kato.context';
import Card from '../../../../components/card/card.component';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import {
  IFarmingItemsList,
  IPoultryList,
  IScreeningFarming,
} from '../../../../types/screening.types';
import {
  colors,
  cropColors,
  selfSufficiencyKeyToIcon,
} from '../../../../consts/other.conts';
import { useUser } from '../../../../contexts/user.context';

const baseClass = 'agriculture-tab';
const translationKey = 'screening.agriculture';

const gaps: Record<string, [number, number]> = {
  'animal-husbandry': [-70, 20],
  poultry: [-80, 40],
  meat: [-80, 20],
  'yearly-meat': [-80, 20],
};
const getBarChartData = (
  data: IFarmingItemsList | IPoultryList,
  key: string,
  t: (key: string) => string,
) => {
  return Object.entries(data).map(([name, values]) => {
    const kh = Number(values.kh || 0);
    const lph = Number(values.lph || 0);
    return {
      name: t(`${translationKey}.${key}.${name}`),
      kh,
      lph,
      total: kh + lph,
    };
  });
};

const getGrowthData = (
  growth: Record<string, { year: number; value: string }[]>,
) => {
  return growth['KPC'].map((item, index) => {
    const result: any = { year: item.year };
    for (const [key, values] of Object.entries(growth)) {
      result[key] = Number(values[index]?.value || 0);
    }
    return result;
  });
};

const AgricultureTab = () => {
  const { t } = useTranslation();
  const { agriculture } = useKato().screeningInfo;
  const { isModulePresent } = useUser();

  const {
    animalHusbandry,
    agriculturalGrowth,
    selfSufficiency,
    poultry,
    meat,
    milk,
    cropArea,
    yearlyMeat,
    milkProduction,
    harvest,
  } = agriculture || {};

  const animalHusbandryData = animalHusbandry
    ? getBarChartData(animalHusbandry, 'animal-husbandry', t)
    : [];
  const growthData = agriculturalGrowth
    ? getGrowthData(agriculturalGrowth)
    : [];
  const poultryData = poultry ? getBarChartData(poultry, 'poultry', t) : [];
  const meatData = meat ? getBarChartData(meat, 'meat', t) : [];
  const milkData = milk
    ? [
        {
          name: t(`${translationKey}.milk.dairy`),
          dairy: milk.dairy || 0,
        },
        {
          name: t(`${translationKey}.milk.milkProduction`),
          milkProduction: milk.milkProduction || 0,
        },
      ]
    : [];
  const cropAreaData = cropArea
    ? [
        {
          name: t(`${translationKey}.kh`),
          forage: Number(cropArea.kh.forage),
          grains: Number(cropArea.kh.grains),
          potato: Number(cropArea.kh.potato),
          carnival: Number(cropArea.kh.carnival),
          vegetables: Number(cropArea.kh.vegetables),
          total:
            +cropArea.kh.forage +
            +cropArea.kh.grains +
            +cropArea.kh.potato +
            +cropArea.kh.carnival +
            +cropArea.kh.vegetables,
        },
        {
          name: t(`${translationKey}.lph`),
          forage: Number(cropArea.lph.forage),
          grains: Number(cropArea.lph.grains),
          potato: Number(cropArea.lph.potato),
          carnival: Number(cropArea.lph.carnival),
          vegetables: Number(cropArea.lph.vegetables),
          total:
            +cropArea.lph.forage +
            +cropArea.lph.grains +
            +cropArea.lph.potato +
            +cropArea.lph.carnival +
            +cropArea.lph.vegetables,
        },
      ]
    : [];
  const yearlyMeatData = yearlyMeat
    ? getBarChartData(yearlyMeat, 'yearly-meat', t)
    : [];

  const milkProductionData = milkProduction
    ? Object.entries(milkProduction).map(([name, values]) => ({
        name: t(`${translationKey}.milk-production.${name}`),
        yearly: Number(values.yearly),
        average: Number(values.average),
      }))
    : [];

  const harvestData = harvest
    ? [
        {
          name: t(`${translationKey}.kh`),

          grains: Number(harvest.kh.grains),
          potato: Number(harvest.kh.potato),
          carnival: Number(harvest.kh.carnival),
          forage: Number(harvest.kh.forage),
          vegetables: Number(harvest.kh.vegetables),
          total:
            +harvest.kh.forage +
            +harvest.kh.grains +
            +harvest.kh.potato +
            +harvest.kh.carnival +
            +harvest.kh.vegetables,
        },
        {
          name: t(`${translationKey}.lph`),
          forage: Number(harvest.lph.forage),
          grains: Number(harvest.lph.grains),
          potato: Number(harvest.lph.potato),
          carnival: Number(harvest.lph.carnival),
          vegetables: Number(harvest.lph.vegetables),
          total:
            +harvest.lph.forage +
            +harvest.lph.grains +
            +harvest.lph.potato +
            +harvest.lph.carnival +
            +harvest.lph.vegetables,
        },
      ]
    : [];

  console.log('cropAreaData ', cropAreaData);
  console.log('yearlyMeatData ', yearlyMeatData);
  console.log('harvestData ', harvestData);

  const renderStackedBarChart = (data: any[], t: any, chartKey: string) => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data}
        margin={{ top: 40, right: 20, left: 20, bottom: 20 }}
        barGap={gaps[chartKey]?.[0] || 0}
        barCategoryGap={gaps[chartKey]?.[1] || 0}
      >
        <CartesianGrid vertical={false} horizontal={true} />
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: '0.625rem', fill: '#818689' }}
        />
        <YAxis hide />
        <Bar dataKey="kh" stackId="a" fill="#118F84" isAnimationActive={false}>
          <LabelList
            dataKey="kh"
            position="insideTop"
            style={{ fill: 'white', fontWeight: 600 }}
            formatter={(val) => val?.toString()}
          />
        </Bar>
        <Bar
          dataKey="lph"
          stackId="a"
          fill="#92D050"
          isAnimationActive={false}
          radius={[4, 4, 0, 0]}
          minPointSize={15}
        >
          <LabelList
            dataKey="lph"
            position="insideTop"
            style={{ fill: 'white', fontWeight: 600 }}
            formatter={(val) => val?.toString()}
          />
        </Bar>
        <Bar dataKey="total" fill="transparent">
          <LabelList
            dataKey="total"
            position="top"
            style={{
              fill: 'var(--color-primary)',
              fontWeight: 600,
            }}
            formatter={(val) => `${val}`}
          />
        </Bar>
        <Tooltip
          cursor={{ fill: 'transparent' }}
          formatter={(value: number, key: string) => [
            value.toLocaleString('ru-RU'),
            t(`${translationKey}.${key}`),
          ]}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderStackedHorizontalBarChart = (
    data: any[],
    t: any,
    chartKey: string,
  ) => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
        // barCategoryGap="20%"
        // barGap={10}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis hide type="category" />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: '0.75rem', fill: '#818689' }}
        />
        <Tooltip
          formatter={(value: number, key: string) =>
            key !== 'total' && [
              `${value.toLocaleString()} га`,
              t(`${translationKey}.${chartKey}.${key}`),
            ]
          }
          cursor={{ fill: 'transparent' }}
        />
        {Object.keys(cropColors).map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            // stackId="a"
            fill={cropColors[key]}
            isAnimationActive={false}
            radius={[0, 8, 8, 0]}
          >
            <LabelList
              dataKey={key}
              position="insideRight"
              style={{ fill: 'white', fontWeight: 600 }}
            />
          </Bar>
        ))}
        {/* Подписи сумм справа от каждой группы */}
        <Bar dataKey="total" fill="transparent">
          <LabelList
            dataKey="total"
            position="right"
            formatter={(val) => `${val} га`}
            style={{
              fill: 'var(--color-primary)',
              fontWeight: 600,
              fontSize: '0.75rem',
              marginLeft: '12rem',
            }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  if (!agriculture) return null;
  return (
    <div className={classes[baseClass]}>
      <div className={classes[`${baseClass}__grid`]}>
        {/* Животноводство */}
        {isModulePresent('animal-husbandry') && (
          <Card title={t(`${translationKey}.animal-husbandry.title`)}>
            {renderStackedBarChart(animalHusbandryData, t, 'animal-husbandry')}
          </Card>
        )}

        {/* Динамика роста сельского хозяйства */}
        {isModulePresent('agricultural-growth') &&
          agriculturalGrowth &&
          growthData && (
            <Card title={t(`${translationKey}.agricultural-growth.title`)}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={growthData}
                  margin={{ top: 30, right: 20, left: 20, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    tick={({ x, y, payload }: any) => {
                      return (
                        <text
                          x={x}
                          y={20}
                          fontSize={'0.75rem'}
                          fill="#818689"
                          textAnchor="middle"
                        >
                          {payload.value}
                        </text>
                      );
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis hide domain={['dataMin', 'auto']} />
                  <Tooltip
                    formatter={(value: number, key: string) => [
                      value.toLocaleString('ru-RU'),
                      t(`${translationKey}.agricultural-growth.${key}`),
                    ]}
                    cursor={{ strokeDasharray: '3 3' }}
                  />
                  {/* <Legend content={<CustomLegend translationKey={translationKey + '.agricultural-growth'} />} /> */}

                  {Object.keys(agriculturalGrowth).map((key) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={colors[key] || '#000'}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      isAnimationActive={false}
                    >
                      <LabelList
                        dataKey={key}
                        position="bottom"
                        style={{ fontSize: '0.7rem', fill: '#000' }}
                        formatter={(val) => `${val?.toLocaleString()}`}
                      />
                    </Line>
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

        {/* Обеспеченность продуктами питания */}
        {isModulePresent('self-sufficiency') && selfSufficiency && (
          <Card title={t(`${translationKey}.self-sufficiency.title`)}>
            <div className={classes[`${baseClass}__self-sufficiency`]}>
              {Object.keys(selfSufficiency).map((key: string) => (
                <div
                  key={key}
                  className={classes[`${baseClass}__self-sufficiency__item`]}
                >
                  <span
                    className={classes[`${baseClass}__self-sufficiency__icon`]}
                  >
                    {
                      selfSufficiencyKeyToIcon[
                        key as keyof IScreeningFarming['selfSufficiency']
                      ]
                    }
                  </span>
                  <span
                    className={classes[`${baseClass}__self-sufficiency__label`]}
                  >
                    {t(`${translationKey}.self-sufficiency.${key}`)}
                  </span>
                  <span
                    className={classes[`${baseClass}__self-sufficiency__value`]}
                  >
                    {selfSufficiency[
                      key as keyof IScreeningFarming['selfSufficiency']
                    ] ?? 0}
                    %
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Птицеводство */}
        {isModulePresent('poultry') && poultryData && (
          <Card title={t(`${translationKey}.poultry.title`)}>
            {renderStackedBarChart(poultryData, t, 'poultry')}
          </Card>
        )}

        {/* Животноводство мясного направления */}
        {isModulePresent('meat') && meatData && (
          <Card title={t(`${translationKey}.meat.title`)}>
            {renderStackedBarChart(meatData, t, 'meat')}
          </Card>
        )}

        {/* КРС молочного направления и производство молока, ежегодно */}
        {isModulePresent('milk') && milkData && (
          <Card title={t(`${translationKey}.milk.title`)}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                layout="vertical"
                data={milkData}
                margin={{ top: 10, right: 20, left: 40, bottom: 10 }}
                barGap={-140}
                barCategoryGap={50}
              >
                <CartesianGrid vertical={false} />
                <XAxis type="number" hide />
                <YAxis hide />
                <Tooltip
                  formatter={(value: number, key: string) => [
                    value.toLocaleString(''),
                    t(`${translationKey}.milk.${key}`),
                  ]}
                  labelFormatter={(label: string) => ''}
                  cursor={{ fill: 'transparent' }}
                />
                <Bar
                  key={milkData[0].name}
                  dataKey="dairy"
                  fill="#118F84"
                  radius={[0, 4, 4, 0]}
                >
                  <LabelList
                    dataKey="dairy"
                    position="insideRight"
                    fill="#fff"
                    style={{ fontWeight: 600 }}
                  />
                </Bar>
                <Bar
                  key={milkData[1].name}
                  dataKey="milkProduction"
                  fill="#92D050"
                  radius={[0, 4, 4, 0]}
                >
                  <LabelList
                    dataKey="milkProduction"
                    position="insideRight"
                    fill="#fff"
                    style={{ fontWeight: 600 }}
                  />
                </Bar>
                {/* <Legend content={<CustomLegend translationKey={translationKey + '.milk'} />} /> */}
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Посевная площадь растениеводства */}
        {isModulePresent('crop-area') && cropAreaData && (
          <Card title={t(`${translationKey}.crop-area.title`)}>
            {renderStackedHorizontalBarChart(cropAreaData, t, 'crop-area')}
          </Card>
        )}

        {/* Ежегодное производство мяса */}
        {isModulePresent('yearly-meat') && yearlyMeatData && (
          <Card
            title={t(`${translationKey}.yearly-meat.title`)}
            subtitle="тонн"
          >
            {renderStackedBarChart(yearlyMeatData, t, 'yearly-meat')}
          </Card>
        )}

        {/* Производство молочной продукции */}
        {isModulePresent('milk-production') && milkProductionData && (
          <Card title={t(`${translationKey}.milk-production.title`)}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={milkProductionData}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                barCategoryGap={10}
                barGap={10}
              >
                <CartesianGrid vertical={false} horizontal={true} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#818689' }}
                />
                <YAxis hide />
                <Tooltip
                  formatter={(value: number, key: string) => [
                    `${value}`,
                    t(`${translationKey}.milk-production.${key}`),
                  ]}
                  cursor={{ fill: 'transparent' }}
                />
                <Bar
                  dataKey="yearly"
                  fill={'#118F84'}
                  isAnimationActive={false}
                  radius={[8, 8, 0, 0]}
                >
                  <LabelList
                    dataKey="yearly"
                    position="top"
                    style={{ fontWeight: 600, fill: 'var(--color-primary)' }}
                  />
                </Bar>
                <Bar
                  dataKey="average"
                  fill={'#92D050'}
                  isAnimationActive={false}
                  radius={[8, 8, 0, 0]}
                >
                  <LabelList
                    dataKey="average"
                    position="top"
                    style={{ fontWeight: 600, fill: 'var(--color-primary)' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Валовый сбор продуктов растениеводства */}
        {isModulePresent('harvest') && harvestData && (
          <Card title={t(`${translationKey}.harvest.title`)}>
            {renderStackedHorizontalBarChart(harvestData, t, 'harvest')}
          </Card>
        )}
      </div>
    </div>
  );
};

export default AgricultureTab;
